/**
 * #1157 E2.4 (Ted #32b) — how many package days a visit draws, and what a
 * household owes once its package has covered what it can.
 *
 * Ted, 2026-10-06: Gingr's "Quick Checkout with Packages" shows one
 * household's 40-day daycare package with 33 visits left covering BOTH of
 * their dogs, and asks whether to check out all of the owner's dogs together.
 * Two things in ALA got that wrong:
 *
 *   1. A booking carrying two dogs drew ONE package day — the checkout always
 *      sent `days: 1`, whatever was on the booking. One day per dog now.
 *   2. The household card's total ignored the package: it summed every
 *      visit's price, so a family whose visits the package covers read
 *      "$94.00" right above a checkout that would collect nothing. The card
 *      now shows what is due AFTER the package, worked out exactly the way
 *      the checkout will draw (the chosen package only; a package that cannot
 *      cover the whole visit covers none of it — the server refuses a partial
 *      draw and the visit is charged in full).
 *
 * The SERVER is the authority on the count: a booking-tied redeem draws one
 * day per dog derived from the booking's own rows
 * (`packages/core/modules/payments-admin/package-days.ts`) and ignores the
 * number sent. This module computes the same number for the preview, the
 * short-package warning and the household total, so the screen agrees with
 * what the server will draw.
 *
 * Pure: no React, no fetch.
 */
import type { CustomerPackage, PackageServiceKind } from './PackageBalance.js';
import { DEFAULT_PACKAGE_CHOICE, resolveChosenPackage, type PackageChoice } from './PackageApplyControl.js';

/**
 * Map a service-type name to the package kind it draws from. Returns null
 * when the service does not support package redemption (e.g. a one-off
 * intake). Moved here unchanged from Bookings.tsx.
 *
 * #1157 — a half-day daycare draws from the HALF-day package
 * (`daycare_half`); any other "daycare" from the full-day one. Ted (10-06):
 * different pricing for half-day and full-day packages, so they are separate
 * balances. Keep in step with the server's `packageKindForServiceName`.
 */
export function packageKindForService(name: string | null | undefined): PackageServiceKind | null {
  if (!name) return null;
  const lower = name.toLowerCase();
  if (isHalfDayService(lower)) return 'daycare_half';
  if (lower.includes('daycare')) return 'daycare';
  if (lower.includes('board') || lower.includes('overnight')) return 'boarding';
  if (lower.includes('grooming')) return 'grooming';
  return null;
}

/** Cache key for the per-(customer, serviceKind) active-package map. */
export function packageKey(customerId: string, kind: PackageServiceKind): string {
  return `${customerId}::${kind}`;
}

/** Package days a full visit draws, per dog. */
export const FULL_VISIT_PACKAGE_DAYS_PER_DOG = 1;

/**
 * Package days a HALF-day daycare visit draws, per dog: one day of a
 * HALF-day package (#1157; Ted answered J16 on 10-06). Keep in step with the
 * server's constant of the same name in
 * `packages/core/modules/payments-admin/package-days.ts` (the authority).
 */
export const HALF_DAY_DAYCARE_PACKAGE_DAYS_PER_DOG = 1;

/**
 * A half-day DAYCARE service, by name: the `daycare-half` engine key or a
 * display name like "Half-Day Daycare". The same rule as the server's
 * `isHalfDayDaycareService` (both words), so a "half" hike is not daycare.
 */
export function isHalfDayService(name: string | null | undefined): boolean {
  const n = (name ?? '').toLowerCase();
  return n.includes('daycare') && n.includes('half');
}

/** Package days ONE dog's visit of this service draws. */
export function packageDaysPerDog(serviceName: string | null | undefined): number {
  return packageKindForService(serviceName) === 'daycare_half'
    ? HALF_DAY_DAYCARE_PACKAGE_DAYS_PER_DOG
    : FULL_VISIT_PACKAGE_DAYS_PER_DOG;
}

/**
 * How many dogs a booking row carries. Read from the LIST row (the schedule
 * endpoint's `dogs` / `dog_names`), never from a single-booking action
 * response, which emits the dog-first block at its empty sentinels. A row
 * that names no dog counts as one: a booking is at least one visit.
 */
export function dogCountOf(row: { dogs?: ReadonlyArray<unknown> | null; dog_names?: ReadonlyArray<string> | null }): number {
  const n = row.dogs?.length || row.dog_names?.length || 0;
  return Math.max(1, n);
}

/** A booking's stay window as the list row carries it (`start_date`/`end_date`, ISO or YYYY-MM-DD). */
export interface StayWindow {
  readonly startDate?: string | null;
  readonly endDate?: string | null;
}

/**
 * Nights in a stay window — the server's `stayNights` (payments-admin
 * `package-days.ts`), which is the boarding price's own count: half-open
 * `[start, end)`, at least one. No window, or one that does not move
 * forward, is one night.
 */
export function stayNights(stay: StayWindow | null | undefined): number {
  const ms = (s: string | null | undefined): number =>
    s ? Date.parse(s.includes('T') ? s : `${s}T00:00:00Z`) : Number.NaN;
  const start = ms(stay?.startDate);
  const end = ms(stay?.endDate);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 1;
  return Math.max(1, Math.round((end - start) / 86_400_000));
}

/**
 * Package days this visit draws: one per dog (see the per-dog constants),
 * times the nights of a BOARDING stay — the server's `packageDaysForBooking`.
 */
export function packageDaysForVisit(
  serviceName: string | null | undefined,
  dogCount: number,
  stay?: StayWindow | null,
): number {
  const nights = packageKindForService(serviceName) === 'boarding' ? stayNights(stay) : 1;
  return Math.max(1, Math.trunc(dogCount)) * packageDaysPerDog(serviceName) * nights;
}

/** A list row's stay window, for `packageDaysForVisit`. */
export function stayWindowOf(row: { start_date?: string | null; end_date?: string | null }): StayWindow {
  return { startDate: row.start_date ?? null, endDate: row.end_date ?? null };
}

export interface HouseholdVisit {
  readonly id: string;
  readonly customerId: string;
  readonly serviceName: string | null;
  readonly totalCents: number;
  readonly dogCount: number;
  /** A boarding stay draws a day per dog per night. */
  readonly stay?: StayWindow | null;
}

export interface HouseholdDue {
  /** Sum of the visits' prices, before any package. */
  readonly totalCents: number;
  /** What the desk will collect once the package has covered what it can. */
  readonly dueCents: number;
  /** Package days the checkout will draw. */
  readonly packageDays: number;
  /** Visits the package covers in full. */
  readonly coveredVisits: number;
}

/**
 * What a household owes after its packages, visit by visit in checkout
 * order, mirroring the checkout exactly:
 *   • only the desk's chosen package for the visit is drawn (`choice`);
 *     a visit with "Apply package" off is charged;
 *   • a visit is covered only if that package has every day it needs; a
 *     package that is short covers none of it (the server refuses a partial
 *     draw and the visit is charged in full);
 *   • a package emptied by an earlier sibling drops out, and the next one
 *     becomes the default — as the live list does after each redeem.
 */
export function householdDueAfterPackages(
  visits: ReadonlyArray<HouseholdVisit>,
  packagesByKey: ReadonlyMap<string, ReadonlyArray<CustomerPackage>>,
  choiceOf: (visitId: string) => PackageChoice = () => DEFAULT_PACKAGE_CHOICE,
): HouseholdDue {
  const remaining = new Map<string, number>();
  let totalCents = 0;
  let dueCents = 0;
  let packageDays = 0;
  let coveredVisits = 0;
  for (const v of visits) {
    totalCents += v.totalCents;
    const kind = packageKindForService(v.serviceName);
    const choice = choiceOf(v.id);
    if (!kind || !choice.apply) {
      dueCents += v.totalCents;
      continue;
    }
    const left = (p: CustomerPackage) => remaining.get(p.id) ?? p.remainingDays;
    const candidates = (packagesByKey.get(packageKey(v.customerId, kind)) ?? []).filter((p) => left(p) > 0);
    const chosen = resolveChosenPackage(candidates, choice);
    const needed = packageDaysForVisit(v.serviceName, v.dogCount, v.stay);
    if (!chosen || left(chosen) < needed) {
      dueCents += v.totalCents;
      continue;
    }
    remaining.set(chosen.id, left(chosen) - needed);
    packageDays += needed;
    coveredVisits += 1;
  }
  return { totalCents, dueCents, packageDays, coveredVisits };
}

function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * The household card's money line. Unchanged ("$94.00") when no package
 * applies; otherwise what the desk will collect, the days the package gives,
 * and what it would have been — "$0.00 due after 2 package days ($94.00
 * before)".
 */
export function householdMoneyLabel(d: HouseholdDue): string {
  if (d.packageDays === 0) return money(d.totalCents);
  return `${money(d.dueCents)} due after ${d.packageDays} package day${d.packageDays === 1 ? '' : 's'} (${money(d.totalCents)} before)`;
}
